import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface CuisineFilterProps {
  selectedCuisines: string[];
  onChange: (cuisines: string[]) => void;
}

const CuisineFilter: React.FC<CuisineFilterProps> = ({ selectedCuisines, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cuisineOptions = [
    { value: 'italian', label: 'Italian' },
    { value: 'chinese', label: 'Chinese' },
    { value: 'mexican', label: 'Mexican' },
    { value: 'japanese', label: 'Japanese' },
    { value: 'american', label: 'American' },
    { value: 'thai', label: 'Thai' },
    { value: 'mediterranean', label: 'Mediterranean' },
    { value: 'indian', label: 'Indian' },
    { value: 'french', label: 'French' },
    { value: 'korean', label: 'Korean' },
    { value: 'vietnamese', label: 'Vietnamese' },
    { value: 'greek', label: 'Greek' },
    { value: 'vegetarian', label: 'Vegetarian' },
    { value: 'vegan', label: 'Vegan' },
    { value: 'seafood', label: 'Seafood' },
    { value: 'steakhouses', label: 'Steakhouse' },
    { value: 'pizza', label: 'Pizza' },
    { value: 'bars', label: 'Bars' },
  ];

  const toggleCuisine = (value: string) => {
    const currentSelection = [...selectedCuisines];
    const index = currentSelection.indexOf(value);
    
    if (index > -1) {
      // Remove if already selected
      currentSelection.splice(index, 1);
    } else {
      // Add if not selected
      currentSelection.push(value);
    }
    
    onChange(currentSelection);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <FilterContainer>
      <FilterHeader>
        <FilterTitle>Cuisine Type</FilterTitle>
        <SelectedCount>{selectedCuisines.length} selected</SelectedCount>
      </FilterHeader>
      
      <SelectionButton onClick={toggleModal}>
        {selectedCuisines.length === 0 
          ? 'Select Cuisines' 
          : cuisineOptions
              .filter(c => selectedCuisines.includes(c.value))
              .map(c => c.label)
              .join(', ')
              .substring(0, 30) + (selectedCuisines.length > 2 ? '...' : '')
        }
      </SelectionButton>
      
      <AnimatePresence>
        {isModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleModal}
          >
            <ModalContainer
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Select Cuisines</ModalTitle>
                <CloseButton onClick={toggleModal}>&times;</CloseButton>
              </ModalHeader>
              
              <OptionsList>
                {cuisineOptions.map((cuisine) => (
                  <OptionItem key={cuisine.value}>
                    <Checkbox 
                      type="checkbox" 
                      id={`cuisine-${cuisine.value}`}
                      checked={selectedCuisines.includes(cuisine.value)}
                      onChange={() => toggleCuisine(cuisine.value)}
                    />
                    <Label htmlFor={`cuisine-${cuisine.value}`}>
                      {cuisine.label}
                    </Label>
                  </OptionItem>
                ))}
              </OptionsList>
              
              <ModalFooter>
                <ClearButton 
                  onClick={() => onChange([])}
                  disabled={selectedCuisines.length === 0}
                >
                  Clear All
                </ClearButton>
                <ApplyButton onClick={toggleModal}>
                  Apply
                </ApplyButton>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </FilterContainer>
  );
};

const FilterContainer = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const FilterTitle = styled.h3`
  font-size: 1.1rem;
  color: var(--text-color);
  margin: 0;
`;

const SelectedCount = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const SelectionButton = styled.button`
  width: 100%;
  background: var(--card-bg);
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background: #e8e8e8;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const OptionsList = styled.div`
  padding: 1rem 1.5rem;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  max-height: 60vh;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
`;

const Label = styled.label`
  cursor: pointer;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
  margin-top: auto;
`;

const ApplyButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    background: var(--accent-color);
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #666;
  padding: 0.75rem 1.5rem;
  font-weight: bold;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:not(:disabled):hover {
    color: #333;
    text-decoration: underline;
  }
`;

export default CuisineFilter;